import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, LogOut, BadgeCheck, Clock, FileText } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import API from "../api";

const Account = () => {
  const { t, tl } = useApp();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get("/orders").then((r) => setOrders(r.data)).catch(() => {}),
      API.get("/quotes/mine").then((r) => setQuotes(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <Link to="/auth" className="inline-block bg-[#1b1b1b] text-white px-7 py-3.5 text-sm tracking-wide">{t("signIn")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-4 md:px-8 py-10 fade-up">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="h-px w-10 bg-[#b08d57] mb-4" />
          <h1 className="font-serif-lux text-4xl">{t("myAccount")}</h1>
          <p className="text-sm text-muted-foreground mt-2">{user.name} · {user.email}</p>
          {user.pro_status === "approved" && (
            <span data-testid="pro-badge" className="inline-flex items-center gap-1.5 mt-3 text-xs bg-[#b08d57]/15 text-[#8a6a2f] px-3 py-1 rounded-full"><BadgeCheck className="h-3.5 w-3.5" strokeWidth={1.8} /> {t("proValidated")}</span>
          )}
          {user.pro_status === "pending" && (
            <span data-testid="pro-badge-pending" className="inline-flex items-center gap-1.5 mt-3 text-xs bg-[#f0ebe2] text-[#555] px-3 py-1 rounded-full"><Clock className="h-3.5 w-3.5" strokeWidth={1.8} /> {t("proPendingBadge")}</span>
          )}
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-sm text-[#555] hover:text-[#8b1e3f] transition-colors">
          <LogOut className="h-4 w-4" strokeWidth={1.5} /> {t("logout")}
        </button>
      </div>

      <h2 className="font-serif-lux text-2xl mb-5">{t("myOrders")}</h2>
      {loading ? (
        <p className="text-muted-foreground text-sm">…</p>
      ) : orders.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <Package className="h-12 w-12 mx-auto mb-4 opacity-30" strokeWidth={1} />
          <p>{t("noOrders")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="border border-border rounded-sm p-5 bg-white">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-sm font-medium">{t("orderNumber")} #{o.id.slice(0, 8).toUpperCase()}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <span className="text-xs bg-[#2f7d4f]/10 text-[#2f7d4f] px-2.5 py-1 rounded-full">{t("paidStatus")}</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {o.items.map((i, idx) => (
                  <img key={idx} src={i.image} alt="" className="w-12 h-16 object-cover rounded-sm" title={tl(i.name)} />
                ))}
              </div>
              <div className="flex justify-between mt-3 text-sm">
                <span className="text-muted-foreground">{o.items.length} {t("products")}</span>
                <span className="font-medium">{o.total.toFixed(2)} €</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {quotes.length > 0 && (
        <section className="mt-12" data-testid="my-quotes">
          <h2 className="font-serif-lux text-2xl mb-5 flex items-center gap-2"><FileText className="h-5 w-5 text-[#b08d57]" strokeWidth={1.5} /> {t("myQuotes")}</h2>
          <div className="space-y-3">
            {quotes.map((q) => (
              <div key={q.id} className="border border-border rounded-sm p-4 bg-white flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm text-[#1b1b1b] line-clamp-1">{q.product_name || q.company}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{q.quantity} u. · {new Date(q.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full ${q.status === "handled" ? "bg-[#2f7d4f]/10 text-[#2f7d4f]" : "bg-[#b08d57]/15 text-[#8a6a2f]"}`}>{q.status === "handled" ? t("quoteStatusHandled") : t("quoteStatusNew")}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Account;
